import React, { useEffect, useState } from 'react';
import { firstNameLastNameFormatter } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/agentColumnHelpers';
import { RecurrenceEnum } from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/constants';
import { SimulationAgentData } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/helpers/types';
import { useTranslation } from 'react-i18next';
import MultiStepModal, { ModalStepT } from 'ui/Modals/MultiStepModal';
import { getTodayDateString } from 'components/inputs/dateHelpers';
import {
    HrSimulation,
    SimulationDateString,
} from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { useTheme, styled, Button, Typography } from '@mui/material';
import { DateString } from 'routes/HumanResourcesModule/business/types';

import {
    AGENT_SOFTWARE_ID,
    JOB_NUMBER,
    MANTY_STATUS,
    ORG_CHART_HR_SOFTWARE_ID,
    RECURRENCE,
} from 'routes/HumanResourcesModule/business/definitions';
import { PAY_COMPONENT } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/helpers/constants';
import { HypothesisTypeSelect } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/AddHypotheses/AgentHypothesisModal/HypothesisTypeSelect';
import { PayComponent } from 'routes/HumanResourcesModule/PayState/helpers/constants';
import { isEmpty } from 'lodash';
import { AgentPayComponentHypothesis } from '../../PayComponent/helpers/types';
import { AgentDepartureHypothesis } from '../../Departure/helpers/types';
import { AgentPayComponentHypothesisMutationParams } from '../../PayComponent/helpers/hooks/agentPayComponentHypothesis';
import AgentConfirmationModal from '../components/AgentConfirmationModal';
import { HypothesisModalSelector } from './HypothesisModalSelector';
import { GradeAdvancementHypothesisMutationParams } from '../../GradeAdvancement/helpers/hooks/gradeAdvancement';
import { GradeAdvancementHypothesis } from '../../GradeAdvancement/helpers/types';
import { AgentDepartureHypothesisMutationParams } from '../../Departure/helpers/hooks/agentDepartureHypothesis';
import { AgentDepartureHypothesisType } from '../../Departure/helpers/constants';
import { getModalTitle } from '../../PayComponent/helpers/dataHelpers';
import { isValidGradeAvancementHypothesis } from '../../GradeAdvancement/helpers/dataHelpers';
import { useAgentAlreadyAffectedByIFSEHypothesis } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/helpers/hooks/agentsSimulation';
import { isValidDepartureDate } from './helpers';
import { HypothesisChoice } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/AddHypotheses/AgentHypothesisModal/constants';

const StyMultiStepModal = styled(MultiStepModal, {
    name: 'StyMultiStepModal',
})({
    '& .paper-static-size': {
        minHeight: '85vh',
        maxHeight: '85vh',
    },
});

export type FormState = {
    agentDepartureHypothesisType: AgentDepartureHypothesisType;
    comment: string;
    departureStartDate: SimulationDateString;
    hasIfseChanges: boolean;
    hasPonctualPrimesChanges: boolean;
    ifseStartDate: SimulationDateString;
    newIfseValue: number;
    isFirstStep: boolean;
    isIfseFormValid: boolean;
    isPonctualPrimesFormValid: boolean;
    isDepartureFormValid: boolean;
    ifseValuesByDates: Record<DateString, number>;
    ponctualPrimeValuesByDates: Record<DateString, number>;
    showConfirmationModal: boolean;
    gradeStartDate: SimulationDateString;
    echelon: string;
    grade: string;
    gradeSalary: number;
    hasGradeChanges: boolean;
};

function useFormState(
    initialState: FormState,
    hrSimulation: HrSimulation,
): [FormState, React.Dispatch<React.SetStateAction<FormState>>] {
    const [state, setState] = useState(initialState);
    const [isIfseFormValid, setIsIfseFormValid] = useState(false);
    const [isDepartureFormValid, setDepartureFormValid] = useState(false);
    const [isPonctualPrimesFormValid, setIsPonctualPrimesFormValid] =
        useState(false);

    useEffect(() => {
        if (state.hasIfseChanges) setIsIfseFormValid(true);
        if (state.hasPonctualPrimesChanges) setIsPonctualPrimesFormValid(true);
    }, [state.hasIfseChanges, state.hasPonctualPrimesChanges]);

    useEffect(() => {
        setDepartureFormValid(
            !isEmpty(state.departureStartDate) &&
                isValidDepartureDate(state.departureStartDate, hrSimulation),
        );
    }, [state.departureStartDate]);

    return [
        {
            ...state,
            isIfseFormValid,
            isPonctualPrimesFormValid,
            isDepartureFormValid,
        },
        setState,
    ];
}

type Props = {
    departureHypothesis: AgentDepartureHypothesis | undefined;
    ifseHypothesis: AgentPayComponentHypothesis | undefined;
    ponctualPrimeHypothesis: AgentPayComponentHypothesis | undefined;
    gradeAdvancementHypothesis: GradeAdvancementHypothesis | undefined;
    setCurrentHypothesisId: () => void;
    show: boolean;
    onClose: () => void;
    hrSimulation: HrSimulation;
    agentData: SimulationAgentData;
    selectedHypothesisType: string | null;
    setSelectedHypothesisType: (value: string | null) => void;
    recurrence: RecurrenceEnum;
    agentDepartureHpothesisCRUDOperations: Partial<{
        createDepartureHypothesis: (
            payload: AgentDepartureHypothesisMutationParams,
        ) => void;
        editDepartureHypothesis: (
            payload: AgentDepartureHypothesisMutationParams,
        ) => void;
        deleteDepartureHypothesis: () => void;
    }>;
    agentPayComponentHpothesisCRUDOperations: Partial<{
        createAgentPayComponentHypothesis: (
            payload: AgentPayComponentHypothesisMutationParams,
        ) => void;
        editAgentPayComponentHypothesis: (
            payload: AgentPayComponentHypothesisMutationParams,
        ) => void;
        deleteAgentPayComponentHypothesis: () => void;
    }>;
    gradeAdvancementHypothesisCRUDOperations: Partial<{
        createGradeAdvancementHypothesis: (
            payload: GradeAdvancementHypothesisMutationParams,
        ) => void;
        editGradeAdvancementHypothesis: (
            payload: GradeAdvancementHypothesisMutationParams,
        ) => void;
        deleteGradeAdvancementHypothesis: () => void;
    }>;
};

const AgentHypothesisModal = ({
    agentData,
    agentDepartureHpothesisCRUDOperations,
    agentPayComponentHpothesisCRUDOperations,
    gradeAdvancementHypothesisCRUDOperations,
    departureHypothesis,
    hrSimulation,
    ifseHypothesis,
    gradeAdvancementHypothesis,
    onClose,
    ponctualPrimeHypothesis,
    recurrence,
    selectedHypothesisType,
    setCurrentHypothesisId,
    setSelectedHypothesisType,
    show,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { alreadyAffectedByIFSE, hypothesisAffectingAgent } =
        useAgentAlreadyAffectedByIFSEHypothesis(
            hrSimulation.id,
            agentData[AGENT_SOFTWARE_ID],
            show,
        );
    const filters = {
        [AGENT_SOFTWARE_ID]: agentData[AGENT_SOFTWARE_ID],
        [JOB_NUMBER]: agentData[JOB_NUMBER],
        [MANTY_STATUS]: agentData[MANTY_STATUS],
        [ORG_CHART_HR_SOFTWARE_ID]: agentData[ORG_CHART_HR_SOFTWARE_ID],
        [PAY_COMPONENT]: PayComponent.PONCTUAL_PRIMES,
        [RECURRENCE]: recurrence,
    };
    const {
        createDepartureHypothesis,
        editDepartureHypothesis,
        deleteDepartureHypothesis,
    } = agentDepartureHpothesisCRUDOperations;
    const {
        createAgentPayComponentHypothesis,
        editAgentPayComponentHypothesis,
        deleteAgentPayComponentHypothesis,
    } = agentPayComponentHpothesisCRUDOperations;

    const {
        createGradeAdvancementHypothesis,
        editGradeAdvancementHypothesis,
        deleteGradeAdvancementHypothesis,
    } = gradeAdvancementHypothesisCRUDOperations;

    const handleSelectHypothesisType = (type: string) => {
        setSelectedHypothesisType(type);
    };

    const agentName = firstNameLastNameFormatter({
        lastName: agentData.agentLastName,
        firstName: agentData.agentFirstName,
    });
    const initHypothesisType =
        recurrence === RecurrenceEnum.RECURRENT
            ? AgentDepartureHypothesisType.SIMPLE_DEPARTURE
            : AgentDepartureHypothesisType.END_OF_CONTRACT;

    const [formState, setFormState] = useFormState(
        {
            departureStartDate:
                departureHypothesis !== undefined
                    ? departureHypothesis.startDate
                    : (getTodayDateString() as SimulationDateString),
            ifseValuesByDates: {},
            ponctualPrimeValuesByDates: {},
            agentDepartureHypothesisType:
                departureHypothesis !== undefined
                    ? departureHypothesis.agentDepartureHypothesisType
                    : initHypothesisType,
            comment: '',
            hasIfseChanges: false,
            hasPonctualPrimesChanges: false,
            isFirstStep: true,
            showConfirmationModal: false,
            isIfseFormValid: false,
            isDepartureFormValid: false,
            isPonctualPrimesFormValid: false,
            newIfseValue:
                ifseHypothesis !== undefined
                    ? ifseHypothesis?.newValuesByDates[
                          Object.keys(ifseHypothesis?.newValuesByDates)[0]
                      ]
                    : 0,
            ifseStartDate: getTodayDateString() as SimulationDateString,
            grade:
                gradeAdvancementHypothesis !== undefined
                    ? gradeAdvancementHypothesis.grade
                    : '',
            echelon:
                gradeAdvancementHypothesis !== undefined
                    ? gradeAdvancementHypothesis.echelon
                    : '',
            gradeSalary:
                gradeAdvancementHypothesis !== undefined
                    ? gradeAdvancementHypothesis.salary
                    : 0,
            gradeStartDate:
                gradeAdvancementHypothesis !== undefined
                    ? gradeAdvancementHypothesis.startDate
                    : (getTodayDateString() as SimulationDateString),
            hasGradeChanges: false,
        },
        hrSimulation,
    );

    const onConfirmationModalChange = (value: boolean) => {
        setFormState((prevState) => ({
            ...prevState,
            showConfirmationModal: value,
        }));
    };
    const firstStep = {
        confirmAction: {
            enabled: selectedHypothesisType !== null,
            label: t('common.next'),
            onClick: () => {
                setCurrentHypothesisId();
                setFormState({ ...formState, isFirstStep: false }); // show confirmation modal and hide first step
            },
        },
        title: agentName,
        stepBackAction: {
            label: t('common.cancel'),
            onClick: onClose,
        },
        node: (
            <HypothesisTypeSelect
                selectedHypothesisType={selectedHypothesisType}
                setSelectedHypothesisType={handleSelectHypothesisType}
                recurrence={recurrence}
                mantyStatus={agentData.mantyStatus}
                departureHypothesis={departureHypothesis}
                ifseHypothesis={ifseHypothesis}
                ponctualPrimeHypothesis={ponctualPrimeHypothesis}
                gradeAdvancementHypothesis={gradeAdvancementHypothesis}
            />
        ),
    };

    const onPonctualPrimesValuesByDatesChange = (
        newValuesByDates: Record<DateString, number>,
    ) => {
        setFormState({
            ...formState,
            ponctualPrimeValuesByDates: newValuesByDates,
            hasPonctualPrimesChanges: true,
        });
    };

    // when a new ifse value or start date is set, we need to update the ifse values by dates,
    // from the startDate of the hypothesis until the end date of the simulation

    useEffect(() => {
        const result: Record<DateString, number> = {};
        const date = new Date(formState.ifseStartDate);
        while (date <= new Date(hrSimulation.endDate)) {
            const monthStr = (date.getMonth() + 1).toString().padStart(2, '0');
            const formattedDate = `${date.getFullYear()}${monthStr}01`;
            result[formattedDate as `${number}${number}${number}`] =
                formState.newIfseValue;
            date.setMonth(date.getMonth() + 1);
        }
        setFormState({
            ...formState,
            hasIfseChanges: true,
            ifseValuesByDates: result,
        });
    }, [formState.newIfseValue, formState.ifseStartDate]);

    const isEditMode =
        (selectedHypothesisType === HypothesisChoice.PRIME &&
            ponctualPrimeHypothesis) ||
        (selectedHypothesisType === HypothesisChoice.IFSE && ifseHypothesis) ||
        (selectedHypothesisType === HypothesisChoice.DEPARTURE &&
            departureHypothesis) ||
        (selectedHypothesisType === HypothesisChoice.GRADE &&
            gradeAdvancementHypothesis);

    const onValidate = () => {
        if (selectedHypothesisType === HypothesisChoice.DEPARTURE) {
            if (departureHypothesis === undefined) {
                createDepartureHypothesis?.({
                    startDate: formState.departureStartDate,
                    agentDepartureHypothesisType:
                        formState.agentDepartureHypothesisType,
                });
            } else {
                editDepartureHypothesis?.({
                    startDate: formState.departureStartDate,
                    agentDepartureHypothesisType:
                        formState.agentDepartureHypothesisType,
                });
            }
        }
        if (
            selectedHypothesisType === HypothesisChoice.PRIME &&
            formState.isPonctualPrimesFormValid
        ) {
            if (ponctualPrimeHypothesis === undefined) {
                createAgentPayComponentHypothesis?.({
                    valuesByDates: formState.ponctualPrimeValuesByDates,
                    comment: formState.comment,
                    payComponent: PayComponent.PONCTUAL_PRIMES,
                });
            } else {
                editAgentPayComponentHypothesis?.({
                    valuesByDates: formState.ponctualPrimeValuesByDates,
                    comment: formState.comment,
                    payComponent: PayComponent.PONCTUAL_PRIMES,
                });
            }
        }
        if (
            selectedHypothesisType === HypothesisChoice.IFSE &&
            formState.hasIfseChanges
        ) {
            if (ifseHypothesis === undefined) {
                createAgentPayComponentHypothesis?.({
                    valuesByDates: formState.ifseValuesByDates,
                    comment: '',
                    payComponent: PayComponent.IFSE_AND_MONTHLY_PRIMES,
                });
            } else {
                editAgentPayComponentHypothesis?.({
                    valuesByDates: formState.ifseValuesByDates,
                    comment: '',
                    payComponent: PayComponent.IFSE_AND_MONTHLY_PRIMES,
                });
            }
        }
        if (
            selectedHypothesisType === HypothesisChoice.GRADE &&
            formState.hasGradeChanges
        ) {
            if (gradeAdvancementHypothesis === undefined) {
                createGradeAdvancementHypothesis?.({
                    salary: formState.gradeSalary,
                    grade: formState.grade,
                    startDate: formState.gradeStartDate,
                    echelon: formState.echelon,
                });
            } else {
                editGradeAdvancementHypothesis?.({
                    salary: formState.gradeSalary,
                    grade: formState.grade,
                    startDate: formState.gradeStartDate,
                    echelon: formState.echelon,
                });
            }
        }
        onClose();
    };

    const onDelete = () => {
        if (selectedHypothesisType === HypothesisChoice.DEPARTURE) {
            deleteDepartureHypothesis?.();
        } else if (
            selectedHypothesisType === HypothesisChoice.IFSE ||
            selectedHypothesisType === HypothesisChoice.PRIME
        ) {
            deleteAgentPayComponentHypothesis?.();
        } else if (selectedHypothesisType === HypothesisChoice.GRADE) {
            deleteGradeAdvancementHypothesis?.();
        }
        onClose();
    };

    const confirmationModal = (
        <AgentConfirmationModal
            onDelete={onDelete}
            showConfirmationModal={formState.showConfirmationModal}
            setShowConfirmationModal={onConfirmationModalChange}
        />
    );

    let isLastStepEnabled: boolean;

    if (selectedHypothesisType === HypothesisChoice.PRIME) {
        isLastStepEnabled = formState.hasPonctualPrimesChanges;
    } else if (selectedHypothesisType === HypothesisChoice.GRADE) {
        isLastStepEnabled = isValidGradeAvancementHypothesis({
            hrSimulation,
            grade: formState.grade,
            echelon: formState.echelon,
            gradeStartDate: formState.gradeStartDate,
        })
            ? formState.hasGradeChanges
            : false;
    } else if (selectedHypothesisType === HypothesisChoice.IFSE) {
        isLastStepEnabled =
            formState.newIfseValue > 0 && formState.hasIfseChanges;
    } else if (selectedHypothesisType === HypothesisChoice.DEPARTURE) {
        isLastStepEnabled = formState.isDepartureFormValid;
    } else {
        isLastStepEnabled = true;
    }

    const lastStep = {
        confirmAction: {
            enabled: isLastStepEnabled,
            onClick: onValidate,
            label: t('common.validate'),
        },
        leftActionGroup: (
            <>
                {isEditMode && (
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() =>
                            setFormState({
                                ...formState,
                                showConfirmationModal: true,
                            })
                        }
                    >
                        {t('common.delete')}
                    </Button>
                )}
                {formState.showConfirmationModal && confirmationModal}
            </>
        ),

        stepBackAction: {
            onClick: () => {
                if (
                    alreadyAffectedByIFSE &&
                    selectedHypothesisType === HypothesisChoice.IFSE
                )
                    return;
                setSelectedHypothesisType(null);
                setFormState({ ...formState, isFirstStep: true });
            },
        },
        title: getModalTitle(agentData, selectedHypothesisType ?? ''),
        node: (
            <HypothesisModalSelector
                hrSimulation={hrSimulation}
                agentData={agentData}
                filters={filters}
                formState={formState}
                setFormState={setFormState}
                onPonctualPrimesValuesByDatesChange={
                    onPonctualPrimesValuesByDatesChange
                }
                ponctualPrimeHypothesis={ponctualPrimeHypothesis}
                freeHypothesisAffectingAgent={hypothesisAffectingAgent}
                recurrence={recurrence}
                selectedHypothesisType={selectedHypothesisType}
            />
        ),
    };

    const potentialConfirmationStep = {
        stepBackAction: {
            onClick: () => {
                setSelectedHypothesisType(null);
                setFormState({ ...formState, isFirstStep: true });
            },
        },
        title: agentName,
        node: (
            <Typography sx={{ m: '0 2rem' }}>
                {t(
                    'humanResources.simulation.agentHypothesis.creationModal.agentAlreadyAffectedByAnotherHypothesis',
                )}
            </Typography>
        ),
    };

    const steps: ModalStepT[] = [firstStep, lastStep];

    if (
        alreadyAffectedByIFSE &&
        selectedHypothesisType === HypothesisChoice.IFSE
    ) {
        steps.splice(1, 0, potentialConfirmationStep);
    }

    return (
        <StyMultiStepModal
            isOpen={show}
            onClose={onClose}
            maxWidth={
                !formState.isFirstStep &&
                selectedHypothesisType === HypothesisChoice.PRIME
                    ? 'lg'
                    : 'md'
            }
            steps={steps}
            PaperProps={{
                sx: { backgroundColor: theme.palette.lightGrey },
            }}
        />
    );
};

export default AgentHypothesisModal;
